import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Terminal, Check, X, Loader2 } from 'lucide-react'

export default function CliAuthPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth()
  const approveCliAuth = useMutation(api.cliApi.approveCliAuth)

  const sessionId = searchParams.get('session')

  const [state, setState] = useState<'pending' | 'approving' | 'approved' | 'error'>('pending')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // CLI auth requires a signed-in account
  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated && sessionId) {
      navigate(`/login?from=${encodeURIComponent(`/cli-auth?session=${sessionId}`)}`, { replace: true })
    }
  }, [isAuthLoading, isAuthenticated, sessionId, navigate])

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-sm border-border bg-card">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <X className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-lg">Session expired</CardTitle>
            <CardDescription>
              Run <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">footage login</code> again to start a new session.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const handleApprove = async () => {
    setState('approving')
    try {
      await approveCliAuth({ sessionId })
      setState('approved')
    } catch (err: unknown) {
      setState('error')
      const msg = err instanceof Error ? err.message : 'Authorization failed'
      if (msg.includes('expired') || msg.includes('not found')) {
        setErrorMessage('Session expired. Run `footage login` again.')
      } else if (msg.includes('already processed')) {
        setErrorMessage('This session has already been authorized.')
      } else {
        setErrorMessage(msg)
      }
    }
  }

  if (state === 'approved') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Helmet><title>CLI Authorized - Footage</title></Helmet>
        <Card className="w-full max-w-sm border-border bg-card">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
              <Check className="h-6 w-6 text-emerald-500" />
            </div>
            <CardTitle className="text-lg">CLI authorized</CardTitle>
            <CardDescription>You can close this tab and return to your terminal.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Helmet><title>CLI Auth Error - Footage</title></Helmet>
        <Card className="w-full max-w-sm border-border bg-card">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <X className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-lg">Authorization failed</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Helmet><title>Authorize CLI - Footage</title></Helmet>
      <Card className="w-full max-w-sm border-border bg-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Terminal className="h-6 w-6 text-foreground" />
          </div>
          <CardTitle className="text-lg">Authorize Footage CLI</CardTitle>
          <CardDescription>
            This will allow the Footage CLI to access your projects.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {user && (
            <div className="flex items-center gap-3 rounded-md border border-border bg-muted/50 p-3">
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="h-8 w-8 rounded-full" />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                  {(user.name || user.email)?.[0]?.toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                {user.name && <p className="text-sm font-medium truncate">{user.name}</p>}
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => window.close()}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleApprove}
              disabled={state === 'approving'}
            >
              {state === 'approving' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Authorize'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
